import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import { 
  Building2, Plus, Settings, Users, MapPin, Briefcase, X, RefreshCw,
  ChevronRight, BarChart3, Shield, Clock, CheckCircle, AlertCircle,
  Home, Activity, Calendar, DollarSign, Edit2, Trash2, Eye, UserPlus,
  TrendingUp, PieChart, Target, Award, LayoutDashboard, Mail, Upload,
  Download, FileText, MoreVertical, Key, Lock, Unlock, Ban, UserX,
  ShieldCheck, Building, MapPinned, Camera, Type, Pen, ClipboardList,
  Archive, ArchiveRestore
} from 'lucide-react';
import { truncateLabel } from '../../utils/text';
// Sub-components extracted on Feb 20, 2026 as part of the 3.9k-line
// component decomposition. See ./group-homes/{views,modals}.js.
import { ManagersView, ShiftsView, AnalyticsView, AdminOverviewView } from './group-homes/views';
import ProgramLifecyclePanel from './group-homes/ProgramLifecyclePanel';
import {
  PostShiftForm, CreateProgramModal, CreateLocationModal,
  AssignManagerModal, CreatePmAccountModal, BulkImportPmModal,
  EditProgramModal, EditLocationModal
} from './group-homes/modals';
// CreateShiftModal is the largest sub-component (~700 lines); lazy-load it
// so employers who never open it don't pay the ~40KB gzipped cost up front.
const CreateShiftModal = lazy(() => import('./group-homes/CreateShiftModal'));

export default function GroupHomesManagement({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // overview, programs, locations, managers, shifts, analytics
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [locations, setLocations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [userRole, setUserRole] = useState('company_admin');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  
  // Modal states
  const [showCreateProgram, setShowCreateProgram] = useState(false);
  const [showCreateLocation, setShowCreateLocation] = useState(false);
  const [editProgram, setEditProgram] = useState(null);
  const [editLocation, setEditLocation] = useState(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const [programFilter, setProgramFilter] = useState('active');
  const [programSearch, setProgramSearch] = useState('');
  const [locFilter, setLocFilter] = useState('active');
  const [locSearch, setLocSearch] = useState('');
  const [selectedLocIds, setSelectedLocIds] = useState([]);
  const [showAssignManager, setShowAssignManager] = useState(false);
  const [showCreateShift, setShowCreateShift] = useState(false);
  const [showCreatePmAccount, setShowCreatePmAccount] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    loadDashboard();
    loadPrograms();
  }, []);

  // Live sync: group-homes stats/programs refresh on relevant changes.
  useLiveRefresh(['programs', 'locations', 'shifts', 'group_homes', 'jobs'], () => {
    loadDashboard();
    loadPrograms();
  });

  useEffect(() => {
    if (activeView === 'analytics') {
      loadAnalytics();
    }
  }, [activeView, analyticsDays]);

  const loadDashboard = async () => {
    setDashboardLoading(true);
    try {
      const response = await apiCall('/api/employer/group-homes/dashboard');
      setDashboardStats(response.stats);
      setUserRole(response.stats.role);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setDashboardLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await apiCall(`/api/employer/group-homes/analytics?days=${analyticsDays}`);
      setAnalyticsData(response);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/employer/group-homes/programs?status=all');
      const all = response.programs || [];
      setAllPrograms(all);
      // Scheduling-safe subset for pickers/modals (mirrors backend default filter)
      setPrograms(all.filter(p => !['archived', 'deleted', 'pending_deletion', 'inactive'].includes(p.status)));
    } catch (error) {
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const loadProgramLocations = async (programId) => {
    try {
      const response = await apiCall(`/api/employer/group-homes/programs/${programId}/locations?status=all`);
      setLocations(response.locations || []);
      setSelectedLocIds([]);
    } catch (error) {
      toast.error('Failed to load locations');
    }
  };

  const programAction = async (program, action) => {
    const labels = { archive: 'archived', restore: 'restored', deactivate: 'deactivated', reactivate: 'reactivated' };
    try {
      await apiCall(`/api/employer/group-homes/programs/${program._id}/${action}`, { method: 'POST' });
      toast.success(`"${truncateLabel(program.name, 30)}" ${labels[action]}.`);
      loadPrograms();
    } catch (error) {
      toast.error(error.message || `Failed to ${action} program`);
    }
  };

  const locationAction = async (location, action) => {
    const labels = { deactivate: 'deactivated', restore: 'restored' };
    try {
      await apiCall(`/api/employer/group-homes/locations/${location._id}/${action}`, { method: 'POST' });
      toast.success(`"${truncateLabel(location.name, 30)}" ${labels[action] || action}.`);
      if (selectedProgram) loadProgramLocations(selectedProgram._id);
    } catch (error) {
      toast.error(error.message || `Failed to ${action}`);
    }
  };

  const deleteLocation = async (location) => {
    if (!window.confirm(`Delete "${truncateLabel(location.name, 40)}"? This hides it permanently (history is preserved for audits).`)) return;
    try {
      await apiCall(`/api/employer/group-homes/locations/${location._id}`, { method: 'DELETE' });
      toast.success(`"${truncateLabel(location.name, 30)}" deleted.`);
      if (selectedProgram) loadProgramLocations(selectedProgram._id);
    } catch (error) {
      toast.error(error.message || 'Failed to delete — archive it instead.');
    }
  };

  const bulkLocationAction = async (action) => {
    try {
      const res = await apiCall('/api/employer/group-homes/locations/bulk-status', {
        method: 'POST',
        body: JSON.stringify({ location_ids: selectedLocIds, action }),
      });
      const skipped = res.skipped || [];
      toast.success(`${(res.updated || []).length} group home(s) ${action}d.${skipped.length ? ` ${skipped.length} skipped (blocking shifts).` : ''}`);
      if (selectedProgram) loadProgramLocations(selectedProgram._id);
    } catch (error) {
      toast.error(error.message || 'Bulk action failed');
    }
  };

  const archiveLocation = async (location) => {
    const doArchive = async (force) => {
      await apiCall(`/api/employer/group-homes/locations/${location._id}/archive`, {
        method: 'POST',
        body: JSON.stringify({ force }),
      });
      toast.success(`"${truncateLabel(location.name, 30)}" archived — hidden from scheduling, history preserved.`);
      if (selectedProgram) loadProgramLocations(selectedProgram._id);
    };
    try {
      await doArchive(false);
    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('upcoming or active shift')) {
        if (window.confirm(`${msg}\n\nArchive anyway?`)) {
          try { await doArchive(true); } catch (e2) { toast.error(e2.message || 'Failed to archive'); }
        }
      } else {
        toast.error(msg || 'Failed to archive location');
      }
    }
  };

  const restoreLocation = async (location) => {
    try {
      await apiCall(`/api/employer/group-homes/locations/${location._id}/restore`, { method: 'POST' });
      toast.success(`"${truncateLabel(location.name, 30)}" restored.`);
      if (selectedProgram) loadProgramLocations(selectedProgram._id);
    } catch (error) {
      toast.error(error.message || 'Failed to restore location');
    }
  };

  const loadManagers = async () => {
    try {
      const response = await apiCall('/api/employer/group-homes/managers');
      setManagers(response.managers || []);
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      company_admin: 'bg-purple-100 text-purple-700',
      agency_admin: 'bg-teal-100 text-teal-700',
      program_manager: 'bg-blue-100 text-blue-700',
      location_manager: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      company_admin: 'Company Admin',
      agency_admin: 'Agency Admin',
      program_manager: 'Program Manager',
      location_manager: 'Location Manager'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[role] || 'bg-slate-100 text-slate-700'}`}>
        {labels[role] || role}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Home className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Admin Dashboard</h2>
                <p className="text-white/80">Enterprise Multi-Staffing</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getRoleBadge(userRole)}
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mt-6">
            {(['company_admin', 'agency_admin'].includes(userRole) 
              ? ['overview', 'admin-overview', 'managers', 'shifts', 'analytics', 'lifecycle']
              : ['overview', 'managers', 'shifts', 'analytics']
            ).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveView(tab)}
                className={`px-4 py-2 rounded-xl font-medium transition ${
                  activeView === tab 
                    ? 'bg-white text-indigo-600' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid={`group-homes-tab-${tab}`}
              >
                {tab === 'analytics' ? '📊 Analytics' : 
                 tab === 'admin-overview' ? '👁 Admin Overview' : 
                 tab === 'lifecycle' ? '⚙ Lifecycle' :
                 tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeView === 'overview' && (
            dashboardLoading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
            ) : dashboardStats ? (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
                    <div className="flex items-center gap-3 mb-3">
                      <Building2 className="w-8 h-8 opacity-80" />
                      </div>
                      <p className="text-3xl font-bold">{dashboardStats.programs?.total || 0}</p>
                      <p className="text-white/80">Total Programs</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
                      <div className="flex items-center gap-3 mb-3">
                        <MapPin className="w-8 h-8 opacity-80" />
                      </div>
                      <p className="text-3xl font-bold">{dashboardStats.locations?.total || 0}</p>
                      <p className="text-white/80">Group Homes</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
                      <div className="flex items-center gap-3 mb-3">
                        <Briefcase className="w-8 h-8 opacity-80" />
                      </div>
                      <p className="text-3xl font-bold">{dashboardStats.shifts?.active || 0}</p>
                      <p className="text-white/80">Active Shifts</p>
                    </div>
                    <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl p-5 text-white">
                      <div className="flex items-center gap-3 mb-3">
                        <Users className="w-8 h-8 opacity-80" />
                      </div>
                      <p className="text-3xl font-bold">{dashboardStats.staffing?.managers || 0}</p>
                      <p className="text-white/80">Managers</p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {(['company_admin', 'agency_admin'].includes(userRole) || userRole === 'program_manager') && (
                        <button
                          onClick={() => {
                            if (programs.length > 0) {
                              setSelectedProgram(programs[0]);
                              setShowCreateLocation(true);
                            } else {
                              toast.error('Create a program first');
                            }
                          }}
                          className="p-4 bg-white border-2 border-dashed border-emerald-200 rounded-xl hover:border-emerald-400 hover:bg-emerald-50 transition flex items-center gap-3"
                        >
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <Home className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-slate-800">Add Group Home</p>
                            <p className="text-sm text-slate-500">New location under program</p>
                          </div>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (locations.length > 0 || programs.length > 0) {
                            setShowCreateShift(true);
                          } else {
                            toast.error('Create a program and location first');
                          }
                        }}
                        className="p-4 bg-white border-2 border-dashed border-amber-200 rounded-xl hover:border-amber-400 hover:bg-amber-50 transition flex items-center gap-3"
                      >
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Calendar className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-slate-800">Post Shift</p>
                          <p className="text-sm text-slate-500">Create staffing request</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Recent Programs */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800">Recent Programs</h3>
                      <button
                        onClick={() => setActiveView('programs')}
                        className="text-sm text-indigo-600 hover:underline"
                      >
                        View All
                      </button>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {programs.slice(0, 3).map(program => (
                        <div key={program._id} className="p-4 hover:bg-slate-50 transition flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-indigo-100 rounded-xl">
                              <Building2 className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-800">{program.name}</h4>
                              <p className="text-sm text-slate-500">{program.service_type}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-slate-800">{program.location_count || 0}</p>
                              <p className="text-xs text-slate-500">Locations</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-slate-800">{program.active_shifts || 0}</p>
                              <p className="text-xs text-slate-500">Active Shifts</p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      ))}
                      {programs.length === 0 && (
                        <div className="p-8 text-center">
                          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500">No programs yet</p>
                          <button
                            onClick={() => setShowCreateProgram(true)}
                            className="mt-3 text-indigo-600 hover:underline"
                          >
                            Create your first program
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Failed to load dashboard data</p>
                  <button onClick={loadDashboard} className="mt-3 text-indigo-600 hover:underline">
                    Try again
                  </button>
                </div>
              )
            )}

              {/* Programs Tab */}
              {activeView === 'programs' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h3 className="text-xl font-bold text-slate-800">Programs</h3>
                    {['company_admin', 'agency_admin'].includes(userRole) && (
                      <button
                        onClick={() => setShowCreateProgram(true)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition flex items-center gap-2"
                      >
                        <Plus className="w-5 h-5" />
                        Create Program
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="text"
                      value={programSearch}
                      onChange={(e) => setProgramSearch(e.target.value)}
                      placeholder="Search programs…"
                      className="px-3 py-2 border border-slate-300 rounded-xl text-sm w-56 focus:ring-2 focus:ring-indigo-500"
                      data-testid="programs-search-input"
                    />
                    <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1" data-testid="programs-status-filter">
                      {['active', 'inactive', 'archived', 'all'].map(f => (
                        <button
                          key={f}
                          onClick={() => setProgramFilter(f)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition ${
                            programFilter === f ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'
                          }`}
                          data-testid={`programs-filter-${f}`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allPrograms.filter(p => {
                      const st = p.status || 'active';
                      const okStatus = programFilter === 'all' ? st !== 'deleted'
                        : programFilter === 'active' ? !['archived', 'deleted', 'pending_deletion', 'inactive'].includes(st)
                        : st === programFilter;
                      return okStatus && (!programSearch || (p.name || '').toLowerCase().includes(programSearch.toLowerCase()));
                    }).map(program => (
                      <div key={program._id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition">
                        <div className="p-5">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl text-white">
                              <Building2 className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-bold text-slate-800 truncate" title={program.name}>{program.name}</h4>
                              <p className="text-sm text-slate-500">{program.service_type}</p>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mb-4 line-clamp-2">{program.description || 'No description'}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{program.location_count || 0} locations</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{program.active_shifts || 0} shifts</span>
                            </div>
                          </div>
                        </div>
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 ${
                            program.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                              : program.status === 'archived' ? 'bg-amber-100 text-amber-700'
                              : program.status === 'inactive' ? 'bg-slate-200 text-slate-600'
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {program.status}
                          </span>
                          <div className="flex items-center gap-2.5">
                            {['company_admin', 'agency_admin'].includes(userRole) && (
                              <>
                                <button
                                  onClick={() => setEditProgram(program)}
                                  className="text-slate-500 hover:text-indigo-600"
                                  title="Edit program"
                                  data-testid={`edit-program-${program._id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                {program.status === 'archived' ? (
                                  <button
                                    onClick={() => programAction(program, 'restore')}
                                    className="text-emerald-600 hover:underline text-sm font-medium flex items-center gap-1"
                                    data-testid={`restore-program-${program._id}`}
                                  >
                                    <ArchiveRestore className="w-4 h-4" /> Restore
                                  </button>
                                ) : program.status === 'inactive' ? (
                                  <button
                                    onClick={() => programAction(program, 'reactivate')}
                                    className="text-emerald-600 hover:underline text-sm font-medium"
                                    data-testid={`reactivate-program-${program._id}`}
                                  >
                                    Reactivate
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => programAction(program, 'deactivate')}
                                      className="text-slate-500 hover:text-slate-700"
                                      title="Deactivate — hide from scheduling without archiving"
                                      data-testid={`deactivate-program-${program._id}`}
                                    >
                                      <Ban className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => programAction(program, 'archive')}
                                      className="text-slate-500 hover:text-amber-600"
                                      title="Archive program"
                                      data-testid={`archive-program-${program._id}`}
                                    >
                                      <Archive className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                            <button
                              onClick={async () => {
                                setSelectedProgram(program);
                                await loadProgramLocations(program._id);
                                setActiveView('locations');
                              }}
                              className="text-indigo-600 hover:underline text-sm font-medium"
                            >
                              View Locations
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Locations Tab */}
              {activeView === 'locations' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-slate-800 truncate max-w-xl" title={selectedProgram?.name}>
                        {selectedProgram ? `${truncateLabel(selectedProgram.name, 40)} - Locations` : 'All Locations'}
                      </h3>
                      {selectedProgram && (
                        <button
                          onClick={() => setSelectedProgram(null)}
                          className="text-sm text-indigo-600 hover:underline"
                        >
                          View all programs
                        </button>
                      )}
                    </div>
                    {(['company_admin', 'agency_admin'].includes(userRole) || userRole === 'program_manager') && selectedProgram && (
                      <button
                        onClick={() => setShowCreateLocation(true)}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition flex items-center gap-2"
                      >
                        <Plus className="w-5 h-5" />
                        Add Group Home
                      </button>
                    )}
                  </div>

                  {selectedProgram && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <input
                        type="text"
                        value={locSearch}
                        onChange={(e) => setLocSearch(e.target.value)}
                        placeholder="Search group homes…"
                        className="px-3 py-2 border border-slate-300 rounded-xl text-sm w-56 focus:ring-2 focus:ring-emerald-500"
                        data-testid="locations-search-input"
                      />
                      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1" data-testid="locations-status-filter">
                        {['active', 'inactive', 'archived', 'all'].map(f => (
                          <button
                            key={f}
                            onClick={() => setLocFilter(f)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition ${
                              locFilter === f ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'
                            }`}
                            data-testid={`locations-filter-${f}`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                      {selectedLocIds.length > 0 && (
                        <div className="flex items-center gap-2 ml-auto bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-1.5" data-testid="locations-bulk-bar">
                          <span className="text-xs font-medium text-indigo-700">{selectedLocIds.length} selected</span>
                          <button onClick={() => bulkLocationAction('archive')} className="text-xs font-medium text-amber-700 hover:underline" data-testid="bulk-archive-btn">Archive</button>
                          <button onClick={() => bulkLocationAction('deactivate')} className="text-xs font-medium text-slate-600 hover:underline" data-testid="bulk-deactivate-btn">Deactivate</button>
                          <button onClick={() => bulkLocationAction('restore')} className="text-xs font-medium text-emerald-700 hover:underline" data-testid="bulk-restore-btn">Restore</button>
                          <button onClick={() => setSelectedLocIds([])} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {locations.filter(location => {
                      const st = location.status || 'operational';
                      const okStatus = locFilter === 'all' ? st !== 'deleted'
                        : locFilter === 'active' ? !['archived', 'deleted', 'inactive'].includes(st)
                        : st === locFilter;
                      return okStatus && (!locSearch || (location.name || '').toLowerCase().includes(locSearch.toLowerCase()));
                    }).map(location => (
                      <div key={location._id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition">
                        <div className="p-5">
                          <div className="flex items-center gap-3 mb-4">
                            <input
                              type="checkbox"
                              checked={selectedLocIds.includes(location._id)}
                              onChange={(e) => setSelectedLocIds(prev => e.target.checked
                                ? [...prev, location._id]
                                : prev.filter(id => id !== location._id))}
                              className="w-4 h-4 rounded border-slate-300 text-indigo-600 shrink-0"
                              data-testid={`select-location-${location._id}`}
                            />
                            <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl text-white">
                              <Home className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-bold text-slate-800 truncate" title={location.name}>{location.name}</h4>
                              <p className="text-sm text-slate-500">Capacity: {location.capacity} beds</p>
                            </div>
                          </div>
                          <div className="text-sm text-slate-600 mb-4">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            {location.address?.street}, {location.address?.city}
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4 text-emerald-500" />
                              <span className="text-slate-600">{location.active_shifts || 0} active</span>
                            </div>
                          </div>
                        </div>
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 ${
                            location.status === 'operational' ? 'bg-emerald-100 text-emerald-700'
                              : location.status === 'archived' ? 'bg-amber-100 text-amber-700'
                              : location.status === 'inactive' ? 'bg-slate-200 text-slate-600'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {location.status}
                          </span>
                          <div className="flex items-center gap-2.5">
                            {['company_admin', 'agency_admin', 'program_manager'].includes(userRole) && (
                              <button
                                onClick={() => setEditLocation(location)}
                                className="text-slate-500 hover:text-indigo-600"
                                title="Edit group home"
                                data-testid={`edit-location-${location._id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            )}
                            {location.status === 'archived' || location.status === 'inactive' ? (
                              <>
                                <button
                                  onClick={() => restoreLocation(location)}
                                  className="text-emerald-600 hover:underline text-sm font-medium flex items-center gap-1"
                                  data-testid={`restore-location-${location._id}`}
                                >
                                  <ArchiveRestore className="w-4 h-4" /> Restore
                                </button>
                                {['company_admin', 'agency_admin'].includes(userRole) && (
                                  <button
                                    onClick={() => deleteLocation(location)}
                                    className="text-slate-400 hover:text-red-600"
                                    title="Delete permanently (only when no upcoming shifts)"
                                    data-testid={`delete-location-${location._id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                {['company_admin', 'agency_admin', 'program_manager'].includes(userRole) && (
                                  <>
                                    <button
                                      onClick={() => locationAction(location, 'deactivate')}
                                      className="text-slate-500 hover:text-slate-700"
                                      title="Deactivate — hide from scheduling without archiving"
                                      data-testid={`deactivate-location-${location._id}`}
                                    >
                                      <Ban className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => archiveLocation(location)}
                                      className="text-slate-500 hover:text-amber-600"
                                      title="Archive — hide from scheduling, keep history"
                                      data-testid={`archive-location-${location._id}`}
                                    >
                                      <Archive className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedLocation(location);
                                    setShowCreateShift(true);
                                  }}
                                  className="text-indigo-600 hover:underline text-sm font-medium"
                                >
                                  Post Shift
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {locations.length === 0 && (
                      <div className="col-span-full p-8 text-center bg-slate-50 rounded-xl">
                        <Home className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No locations in this program</p>
                        {selectedProgram && (
                          <button
                            onClick={() => setShowCreateLocation(true)}
                            className="mt-3 text-indigo-600 hover:underline"
                          >
                            Add first group home
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Managers Tab */}
              {activeView === 'managers' && (
                <ManagersView 
                  userRole={userRole}
                  programs={programs}
                  onAssignManager={() => {
                    loadManagers();
                    setShowAssignManager(true);
                  }}
                  onCreatePmAccount={() => setShowCreatePmAccount(true)}
                  onBulkImport={() => setShowBulkImport(true)}
                  onCreateProgram={() => setShowCreateProgram(true)}
                />
              )}

              {/* Shifts Tab */}
              {activeView === 'shifts' && (
                <ShiftsView 
                  programs={programs}
                  userRole={userRole}
                  onCreateShift={() => setShowCreateShift(true)}
                />
              )}

              {/* Admin Overview Tab - Company Admin Only */}
              {activeView === 'admin-overview' && ['company_admin', 'agency_admin'].includes(userRole) && (
                <AdminOverviewView onCreateProgram={() => setShowCreateProgram(true)} />
              )}

              {/* Analytics Tab */}
              {activeView === 'lifecycle' && <ProgramLifecyclePanel />}

              {activeView === 'analytics' && (
                <AnalyticsView 
                  analyticsData={analyticsData}
                  analyticsDays={analyticsDays}
                  setAnalyticsDays={setAnalyticsDays}
                  onRefresh={loadAnalytics}
                />
              )}
        </div>

        {/* Edit Program Modal */}
        {editProgram && (
          <EditProgramModal
            program={editProgram}
            onClose={() => setEditProgram(null)}
            onUpdated={() => {
              loadPrograms();
              setEditProgram(null);
            }}
          />
        )}

        {/* Edit Group Home Modal */}
        {editLocation && (
          <EditLocationModal
            location={editLocation}
            onClose={() => setEditLocation(null)}
            onUpdated={() => {
              if (selectedProgram) loadProgramLocations(selectedProgram._id);
              setEditLocation(null);
            }}
          />
        )}

        {/* Create Program Modal */}
        {showCreateProgram && (
          <CreateProgramModal
            onClose={() => setShowCreateProgram(false)}
            onCreated={() => {
              loadPrograms();
              loadDashboard();
              setShowCreateProgram(false);
            }}
          />
        )}

        {/* Create Location Modal */}
        {showCreateLocation && selectedProgram && (
          <CreateLocationModal
            program={selectedProgram}
            onClose={() => setShowCreateLocation(false)}
            onCreated={() => {
              loadProgramLocations(selectedProgram._id);
              loadDashboard();
              setShowCreateLocation(false);
            }}
          />
        )}

        {/* Create Shift Modal (lazy-loaded — Suspense-wrapped for the initial chunk fetch) */}
        {showCreateShift && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="text-white">Loading…</div></div>}>
            <CreateShiftModal
              programs={programs}
              selectedProgram={selectedProgram}
              selectedLocation={selectedLocation}
              onClose={() => {
                setShowCreateShift(false);
                setSelectedLocation(null);
              }}
              onCreated={() => {
                loadDashboard();
                setShowCreateShift(false);
                setSelectedLocation(null);
              }}
            />
          </Suspense>
        )}

        {/* Assign Manager Modal */}
        {showAssignManager && (
          <AssignManagerModal
            programs={programs}
            onClose={() => setShowAssignManager(false)}
            onAssigned={() => {
              loadDashboard();
              setShowAssignManager(false);
            }}
          />
        )}

        {/* Create PM Account Modal */}
        {showCreatePmAccount && (
          <CreatePmAccountModal
            programs={programs}
            onClose={() => setShowCreatePmAccount(false)}
            onCreated={() => {
              loadDashboard();
              loadManagers();
              setShowCreatePmAccount(false);
            }}
          />
        )}

        {/* Bulk Import PM Modal */}
        {showBulkImport && (
          <BulkImportPmModal
            onClose={() => setShowBulkImport(false)}
            onImported={() => {
              loadDashboard();
              loadManagers();
              setShowBulkImport(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
